import { capitalize } from '@ca/common'
import DetailRow from '@ui/elements/DetailRow'
import { PaymentStatusAmounts } from '@order/hooks/usePaymentStatusAmounts'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import PaymentAmount from './PaymentAmount'
import { PaymentDetails } from '@order/hooks/usePayments'
import Loader from '@ui/elements/Loader'
import CartUtils from '@order/CartUtils'
import { usePaymentMethodSettings } from '@order/hooks/usePaymentMethods'

export default function PaymentDetailsComponent({
  amounts,
  payment,
  fee,
  refundApplied,
  globemedApplied,
  restAmount,
}: {
  amounts: PaymentStatusAmounts
  payment: PaymentDetails
  fee?: number
  refundApplied?: number | null
  globemedApplied?: number | null
  restAmount?: number | null
}) {
  const { t } = useTranslation(['status', 'common', 'payment'])

  const { left, toPay, paymentAmount } = amounts

  const settings = usePaymentMethodSettings(payment.method, {
    provider: payment.provider,
  })

  const isFinal = CartUtils.payments.isFinalStatus(payment)

  const finalToPay = fee ? toPay + fee : toPay

  const showPercentage = payment.method !== 'invoice'

  const hasGlobemedApplied = globemedApplied != null
  const hasRefundApplied = refundApplied != null
  const hasLeadingRow = hasGlobemedApplied || hasRefundApplied
  const isFullyCovered = restAmount === 0

  // When metadata is available, cap prepayment at restAmount and derive collect from the remainder
  const effectivePrepayment = restAmount != null
    ? Math.min(paymentAmount, restAmount)
    : paymentAmount
  const effectiveLeft = restAmount != null
    ? Math.max(0, restAmount - effectivePrepayment)
    : left

  return (
    <View className="flex flex-col gap-0 overflow-hidden rounded-xl border border-black/10">
      {hasGlobemedApplied && (
        <DetailRow
          first
          size="sm"
          icon="shield-check-filled"
          title={capitalize(t('payment:status.details.globemed.label'))}
        >
          <PaymentAmount
            variant="total"
            value={globemedApplied}
            total={finalToPay}
            showPercentage={showPercentage}
          />
        </DetailRow>
      )}
      {hasRefundApplied && (
        <DetailRow
          first={!hasGlobemedApplied}
          size="sm"
          icon="cash-banknote-move-back"
          title={capitalize(t('payment:status.details.refundBalance.label'))}
        >
          <PaymentAmount
            variant={isFullyCovered ? 'paid' : 'total'}
            value={refundApplied}
            total={finalToPay}
            showPercentage={showPercentage}
          />
        </DetailRow>
      )}
      {settings.updates && (
        <DetailRow
          first={!hasLeadingRow}
          size="sm"
          iconComponent={!isFinal && <Loader loading />}
          icon={isFinal ? 'circle-check-filled' : undefined}
          title={capitalize(t('payment:status.details.prepayment.label'))}
        >
          <PaymentAmount
            variant={isFinal ? 'paid' : 'processing'}
            value={effectivePrepayment}
            total={toPay}
            showPercentage={showPercentage}
          />
        </DetailRow>
      )}
      {!settings.updates && (
        <DetailRow
          first={!hasLeadingRow}
          size="sm"
          icon="shopping-bag"
          title={capitalize(t('payment:status.details.order.label'))}
        >
          <PaymentAmount
            variant="total"
            value={toPay}
            total={finalToPay}
            showPercentage={showPercentage}
          />
        </DetailRow>
      )}
      {settings.custom && (
        <DetailRow
          size="sm"
          icon={effectiveLeft ? 'exclamation-circle' : 'check'}
          title={t('payment:status.details.collect.label')}
        >
          <PaymentAmount
            variant="left"
            value={effectiveLeft}
            total={finalToPay}
            showPercentage={showPercentage}
          />
        </DetailRow>
      )}
      {fee !== undefined && (
        <DetailRow
          size="sm"
          icon="coin-euro"
          title={capitalize(t('payment:status.details.fee.label'))}
        >
          <PaymentAmount
            variant="total"
            value={fee}
            total={finalToPay}
            showPercentage={showPercentage}
          />
        </DetailRow>
      )}
      <DetailRow
        size="sm"
        stronger
        icon="shopping-bag-filled"
        title={capitalize(t('payment:status.details.total.label'))}
      >
        <PaymentAmount
          variant="total"
          value={finalToPay}
          total={finalToPay}
          showPercentage={showPercentage}
        />
      </DetailRow>
    </View>
  )
}
